import { useEffect, useState, type FormEvent } from 'react';
import {
  BellRing,
  CircleCheck,
  DatabaseBackup,
  HardDrive,
  Mail,
  MemoryStick,
  Save,
  ServerCog,
  ShieldAlert
} from 'lucide-react';
import { readToken, superadminTokenKey } from '../../app/session';
import { ApiError, apiRequest } from '../../lib/api';

interface SystemData {
  runtime: {
    status: string;
    appVersion: string;
    nodeVersion: string;
    platform: string;
    uptimeSeconds: number;
    memoryUsedBytes: number;
    memoryTotalBytes: number;
    diskFreeBytes: number;
    diskTotalBytes: number;
    environment: string;
    smtpConfigured: boolean;
    pushConfigured: boolean;
    mysqldumpPath: string;
    mysqlPath: string;
  };
  services: Array<{
    serviceKey: string;
    instanceId: string;
    status: string;
    startedAt: string;
    lastSeenAt: string;
    processedCount: number;
    failedCount: number;
    fresh: boolean;
  }>;
  platformBackup: {
    id: string;
    status: string;
    verificationStatus: string;
    restoreTestStatus: string;
    createdAt: string;
    restoreTestedAt: string | null;
    offsiteStatus: string;
    offsiteCopiedAt: string | null;
  } | null;
  settings: {
    platformName: string;
    supportEmail: string;
    defaultEmployeeLimit: number;
    backupRetentionDays: number;
    maintenanceMode: boolean;
    updatedAt: string;
  };
  activity: Array<{
    id: number;
    action: string;
    companyName: string | null;
    actorName: string | null;
    createdAt: string;
  }>;
}

function humanBytes(value: number): string {
  return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function humanUptime(value: number): string {
  const days = Math.floor(value / 86_400);
  const hours = Math.floor((value % 86_400) / 3_600);
  return days ? `${days} d ${hours} h` : `${hours} h`;
}

export function SystemPage() {
  const token = readToken(superadminTokenKey);
  const [data, setData] = useState<SystemData | null>(null);
  const [settings, setSettings] = useState<SystemData['settings'] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const worker = data?.services.find((service) => service.serviceKey === 'notification-worker');

  async function load() {
    try {
      const response = await apiRequest<SystemData>('/api/v1/superadmin/system', {
        accessToken: token
      });
      setData(response);
      setSettings(response.settings);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Status sustava nije dostupan.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await apiRequest('/api/v1/superadmin/system/settings', {
        method: 'PATCH',
        accessToken: token,
        body: JSON.stringify(settings)
      });
      await load();
      setMessage('Postavke sustava su spremljene.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Postavke nisu spremljene.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <span className="eyebrow">Platforma i nadzor</span>
          <h1>Sustav</h1>
          <p>Postavke koje se mijenjaju bez diranja koda i živi status servera.</p>
        </div>
      </header>

      <section className="system-health-grid">
        <article><span className="health-icon is-good"><ServerCog /></span><div><small>API proces</small><strong>{data?.runtime.status ?? 'Učitavam'}</strong><em>{data ? `v${data.runtime.appVersion} · radi ${humanUptime(data.runtime.uptimeSeconds)}` : '—'}</em></div></article>
        <article><span className="health-icon"><MemoryStick /></span><div><small>Memorija servera</small><strong>{data ? humanBytes(data.runtime.memoryUsedBytes) : '—'}</strong><em>{data ? `od ${humanBytes(data.runtime.memoryTotalBytes)}` : '—'}</em></div></article>
        <article><span className="health-icon"><HardDrive /></span><div><small>Slobodan disk</small><strong>{data ? humanBytes(data.runtime.diskFreeBytes) : '—'}</strong><em>{data ? `od ${humanBytes(data.runtime.diskTotalBytes)}` : '—'}</em></div></article>
        <article><span className={`health-icon ${data?.runtime.smtpConfigured ? 'is-good' : 'is-warning'}`}><Mail /></span><div><small>E-mail slanje</small><strong>{data?.runtime.smtpConfigured ? 'Spremno' : 'Nije podešeno'}</strong><em>SMTP</em></div></article>
        <article><span className={`health-icon ${data?.runtime.pushConfigured ? 'is-good' : 'is-warning'}`}><BellRing /></span><div><small>Push poruke</small><strong>{data?.runtime.pushConfigured ? 'Spremno' : 'Nije podešeno'}</strong><em>VAPID</em></div></article>
        <article><span className="health-icon"><DatabaseBackup /></span><div><small>Backup alat</small><strong>{data?.runtime.mysqldumpPath ?? '—'}</strong><em>mysqldump</em></div></article>
        <article><span className={`health-icon ${worker?.fresh && worker.status !== 'STOPPED' ? 'is-good' : 'is-warning'}`}><BellRing /></span><div><small>Notification worker</small><strong>{worker?.fresh ? worker.status : 'Nema heartbeat'}</strong><em>{worker ? `${worker.processedCount} obrađeno · ${worker.failedCount} grešaka` : 'Pokreni worker proces'}</em></div></article>
        <article><span className={`health-icon ${data?.platformBackup?.restoreTestStatus === 'SUCCEEDED' ? 'is-good' : 'is-warning'}`}><DatabaseBackup /></span><div><small>Dokaz povrata</small><strong>{data?.platformBackup?.restoreTestStatus === 'SUCCEEDED' ? 'Potvrđen' : 'Nije potvrđen'}</strong><em>{data?.platformBackup ? `integritet: ${data.platformBackup.verificationStatus}` : 'Nema potpunog backupa'}</em></div></article>
        <article><span className={`health-icon ${data?.platformBackup?.offsiteStatus === 'SUCCEEDED' ? 'is-good' : 'is-warning'}`}><DatabaseBackup /></span><div><small>Offsite backup</small><strong>{data?.platformBackup?.offsiteStatus === 'SUCCEEDED' ? 'Potvrđen' : 'Nije potvrđen'}</strong><em>{data?.platformBackup?.offsiteCopiedAt ? new Intl.DateTimeFormat('hr-HR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(data.platformBackup.offsiteCopiedAt)) : 'Podesi odvojenu lokaciju'}</em></div></article>
      </section>

      {message && <div className="form-success page-message">{message}</div>}
      {error && <div className="form-error page-message">{error}</div>}
      <section className="system-grid">
        <form className="panel system-settings" onSubmit={save}>
          <div className="panel__toolbar"><div><h2>Postavke platforme</h2><p className="muted">Vrijede kao zadane vrijednosti za nove firme.</p></div></div>
          {settings && (
            <div className="settings-form">
              <label className="field"><span>Naziv platforme</span><span className="field__control"><input value={settings.platformName} onChange={(event) => setSettings({ ...settings, platformName: event.target.value })} required /></span></label>
              <label className="field"><span>E-mail podrške</span><span className="field__control"><input type="email" value={settings.supportEmail} onChange={(event) => setSettings({ ...settings, supportEmail: event.target.value })} /></span></label>
              <div className="form-grid">
                <label className="field"><span>Zadani limit korisnika</span><span className="field__control"><input type="number" min={1} max={500} value={settings.defaultEmployeeLimit} onChange={(event) => setSettings({ ...settings, defaultEmployeeLimit: Number(event.target.value) })} required /></span></label>
                <label className="field"><span>Čuvanje backupa (dani)</span><span className="field__control"><input type="number" min={1} max={3650} value={settings.backupRetentionDays} onChange={(event) => setSettings({ ...settings, backupRetentionDays: Number(event.target.value) })} required /></span></label>
              </div>
              <label className="toggle-row">
                <span className={settings.maintenanceMode ? 'is-on' : ''}><i /></span>
                <input type="checkbox" checked={settings.maintenanceMode} onChange={(event) => setSettings({ ...settings, maintenanceMode: event.target.checked })} />
                <div><strong>Način održavanja</strong><small>Priprema globalnu obavijest korisnicima.</small></div>
              </label>
              <button className="button button--primary button--full" disabled={busy}><Save /> {busy ? 'Spremam…' : 'Spremi postavke'}</button>
            </div>
          )}
        </form>

        <article className="panel">
          <div className="panel__toolbar"><div><h2>Audit zapis</h2><p className="muted">Zadnjih 50 bitnih akcija superadmina.</p></div><ShieldAlert className="toolbar-icon" /></div>
          <div className="audit-list audit-list--scroll">
            {data?.activity.map((entry) => (
              <div key={entry.id}>
                <span className="audit-check"><CircleCheck /></span>
                <span><strong>{entry.action}</strong><small>{entry.companyName ?? 'Platforma'} · {new Intl.DateTimeFormat('hr-HR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(entry.createdAt))}</small></span>
              </div>
            ))}
            {data && !data.activity.length && <div className="table-empty compact-empty">Nema aktivnosti.</div>}
          </div>
        </article>
      </section>
    </>
  );
}
